import {
    useFetchMapTemplateDatasetsExtendedQuery,
    useFetchMapTemplateQuery,
} from "@biggeo/bg-server-lib/studio";
import {
    EmptyScreen,
    ExitIntentModal,
    Stack,
    Typography,
    useDataGridOptions,
} from "@biggeo/bg-ui/lab";
import { AddOutline } from "@biggeo/bg-ui/lab/icons";
import isEmpty from "lodash/isEmpty";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { compose } from "redux";
import { modalActions } from "../../../modal/redux/model";
import MapDatasetsConfigGrid from "./MapDatasetsConfigGrid";
import MapTemplateManageCard from "./MapTemplateManageCard";

const No_DATASET_INFO =
    "You don't have any datasets from your data management area enabled for this map template.";

interface IMapDatasetsConfigView {
    readonly mapTemplateId: string;
    readonly manageMapTemplate: () => void;
    readonly hideBanner?: boolean;
}

const MapDatasetsConfigView = ({
    mapTemplateId,
    manageMapTemplate,
    hideBanner = false,
}: IMapDatasetsConfigView) => {
    const {
        dataGridFetchInputProps: { sortModel, ...rest },
        filterSearchPaginateProps,
    } = useDataGridOptions(20);

    const reduxDispatch = useDispatch();
    const openModal = compose(reduxDispatch, modalActions.openModal);
    const closeModal = compose(reduxDispatch, modalActions.closeModal);

    const {
        queryReturn: { data: mapTemplateData, loading: mapTemplateLoading },
    } = useFetchMapTemplateQuery({
        variables: {
            id: mapTemplateId,
        },
    });

    const {
        queryReturn: {
            data: mapTemplateDatasetsData,
            loading: mapTemplateDatasetsLoading,
            refetch,
        },
    } = useFetchMapTemplateDatasetsExtendedQuery({
        variables: {
            input: {
                fkMapTemplateId: mapTemplateId,
                options: rest,
            },
        },
    });

    const open = () => {
        openModal({
            modalType: "new-dialog",
            component: (
                <ExitIntentModal
                    subtitle={
                        "You're about to leave this map template, consider creating a saved view to save your progress."
                    }
                    discard={closeModal}
                    save={() => {
                        closeModal();
                        manageMapTemplate();
                    }}
                    buttons={{
                        text1: "Nevermind",
                        text2: "Continue",
                    }}
                    withLoading={false}
                />
            ),
            dialogProps: {
                variant: "centered-small",
            },
        });
    };

    return (
        <Stack width="100%" height="100%" gap={2} minHeight={0}>
            {mapTemplateData && !hideBanner && (
                <MapTemplateManageCard
                    id={mapTemplateId}
                    name={mapTemplateData.fetchMapTemplate.name}
                    thumbnail={
                        mapTemplateData.fetchMapTemplate.img || undefined
                    }
                    manage={open}
                />
            )}

            <MapDatasetsConfigGrid
                mapTemplateDatasets={
                    mapTemplateDatasetsData?.fetchMapTemplateDatasetsExtended ||
                    []
                }
                refetchMapTemplateDatasets={() => refetch()}
                totalRows={
                    mapTemplateDatasetsData?.fetchMapTemplateDatasetsExtended
                        .length || 0
                }
                filterSearchPaginateProps={filterSearchPaginateProps}
                loading={mapTemplateLoading || mapTemplateDatasetsLoading}
                manageMapTemplate={manageMapTemplate}
                noDatasetInfo={No_DATASET_INFO}
            />
        </Stack>
    );
};

export default MapDatasetsConfigView;
